/**
 * ConceptGate API functions
 * Trade validation with configurable rules library
 */
import { ENDPOINTS } from './endpoints';
import { getRequestBackendDataV2, postRequestBackendDataV2, deleteRequestBackendDataV2 } from './core';

// Toggle this to switch between mock and real API
const USE_MOCK = false;

// ============================================================================
// ENUMS & TYPES (matching backend)
// ============================================================================

export const Direction = {
  LONG: 'LONG',
  SHORT: 'SHORT',
};

export const ValidationStatus = {
  GO: 'GO',
  NO_GO: 'NO_GO',
  NEEDS_INFO: 'NEEDS_INFO',
};

export const RuleExecutionStatus = {
  PASSED: 'PASSED',
  FAILED: 'FAILED',
  SKIPPED: 'SKIPPED',
  ERROR: 'ERROR',
};

export const RuleType = {
  HARDCODED: 'HARDCODED',
  LLM_AGENT: 'LLM_AGENT',
};

export const RuleCategory = {
  RISK: 'risk',
  TECHNICAL: 'technical',
  TIMING: 'timing',
  PSYCHOLOGY: 'psychology',
};

// ============================================================================
// ACCOUNT API
// ============================================================================

/**
 * Get user's account (equity, currency)
 */
export async function getConceptGateAccount(auth, callback) {
  if (USE_MOCK) {
    callback({ equity: 100000, currency: 'USD' });
    return;
  }
  await getRequestBackendDataV2(auth, callback, ENDPOINTS.CONCEPTGATE.ACCOUNT);
}

/**
 * Update user's account
 */
export async function updateConceptGateAccount(auth, callback, data) {
  if (USE_MOCK) {
    callback({ ...data, updatedAt: new Date().toISOString() });
    return;
  }
  await postRequestBackendDataV2(auth, callback, ENDPOINTS.CONCEPTGATE.ACCOUNT, data, 'PUT');
}

// ============================================================================
// USAGE API
// ============================================================================

/**
 * Get usage summary (cost, tokens)
 */
export async function getConceptGateUsageSummary(auth, callback, days = 7) {
  if (USE_MOCK) {
    callback({ totalCost: 1.25, totalTokens: 150000, validationCount: 45 });
    return;
  }
  await getRequestBackendDataV2(auth, callback, `${ENDPOINTS.CONCEPTGATE.USAGE_SUMMARY}?days=${days}`);
}

// ============================================================================
// TRADE API
// ============================================================================

/**
 * Get current trade
 */
export async function getConceptGateTrade(auth, callback, instrument = null) {
  if (USE_MOCK) {
    callback(null);
    return;
  }
  let endpoint = ENDPOINTS.CONCEPTGATE.TRADE;
  if (instrument) {
    endpoint += `?instrument=${encodeURIComponent(instrument)}`;
  }
  await getRequestBackendDataV2(auth, callback, endpoint);
}

/**
 * Save/update trade
 */
export async function saveConceptGateTrade(auth, callback, tradeSpec) {
  if (USE_MOCK) {
    callback({ ...tradeSpec, id: 'mock-trade-id' });
    return;
  }
  await postRequestBackendDataV2(auth, callback, ENDPOINTS.CONCEPTGATE.TRADE, tradeSpec);
}

/**
 * Delete current trade
 * @param {Object} auth - Firebase auth object
 * @param {Function} callback - Callback function to handle result
 * @param {string} instrument - Optional instrument to delete trade for
 */
export async function deleteConceptGateTrade(auth, callback, instrument = null) {
  if (USE_MOCK) {
    callback({ success: true });
    return;
  }
  let endpoint = ENDPOINTS.CONCEPTGATE.TRADE;
  if (instrument) {
    endpoint += `?instrument=${encodeURIComponent(instrument)}`;
  }
  await deleteRequestBackendDataV2(auth, callback, endpoint);
}

// ============================================================================
// LIBRARY RULES API (Admin-owned)
// ============================================================================

/**
 * Get all library rules
 * @param {string} instrument - Optional filter by instrument
 * @param {string} category - Optional filter by category (risk, technical, timing, psychology)
 * @param {boolean} includeDisabled - Optional, admin-only, include disabled rules
 */
export async function getLibraryRules(auth, callback, { instrument, category, includeDisabled } = {}) {
  if (USE_MOCK) {
    callback(getMockLibraryRules());
    return;
  }
  let endpoint = ENDPOINTS.CONCEPTGATE.LIBRARY_RULES;
  const params = [];
  if (instrument) params.push(`instrument=${encodeURIComponent(instrument)}`);
  if (category) params.push(`category=${encodeURIComponent(category)}`);
  if (includeDisabled) params.push('includeDisabled=true');
  if (params.length) endpoint += '?' + params.join('&');

  await getRequestBackendDataV2(auth, callback, endpoint);
}

/**
 * Get a specific library rule
 */
export async function getLibraryRule(auth, callback, ruleId) {
  if (USE_MOCK) {
    const rules = getMockLibraryRules();
    callback(rules.find(r => r.id === ruleId) || null);
    return;
  }
  await getRequestBackendDataV2(auth, callback, ENDPOINTS.CONCEPTGATE.LIBRARY_RULE(ruleId));
}

/**
 * Update library rule enabled status (admin only)
 */
export async function updateLibraryRuleEnabled(auth, callback, ruleId, enabled) {
  if (USE_MOCK) {
    callback({ success: true });
    return;
  }
  await postRequestBackendDataV2(
    auth,
    callback,
    ENDPOINTS.CONCEPTGATE.LIBRARY_RULE_ENABLED(ruleId),
    { enabled },
    'PATCH'
  );
}

/**
 * Update a library rule (admin only)
 * @param {Object} auth - Firebase auth object
 * @param {Function} callback - Callback function
 * @param {string} ruleId - UUID of the library rule
 * @param {Object} data - Fields to update (name, description, category, enabled, executionOrder, failFast, defaultConfigJson, agentPrompt, agentModel)
 */
export async function updateLibraryRule(auth, callback, ruleId, data) {
  if (USE_MOCK) {
    callback({ success: true, ...data });
    return;
  }
  await postRequestBackendDataV2(
    auth,
    callback,
    ENDPOINTS.CONCEPTGATE.LIBRARY_RULE(ruleId),
    data,
    'PUT'
  );
}

// ============================================================================
// USER RULES API (User selections from library)
// ============================================================================

/**
 * Get user's selected rules
 * @param {string} instrument - Optional filter by instrument
 */
export async function getUserRules(auth, callback, { instrument } = {}) {
  if (USE_MOCK) {
    callback([]);
    return;
  }
  let endpoint = ENDPOINTS.CONCEPTGATE.USER_RULES;
  if (instrument) endpoint += `?instrument=${encodeURIComponent(instrument)}`;

  await getRequestBackendDataV2(auth, callback, endpoint);
}

/**
 * Add a rule from library to user's selections
 */
export async function addUserRule(auth, callback, { libraryRuleId, instrument, customConfigJson }) {
  if (USE_MOCK) {
    callback({ id: 'mock-user-rule-id', libraryRuleId, instrument, enabled: true });
    return;
  }
  await postRequestBackendDataV2(auth, callback, ENDPOINTS.CONCEPTGATE.USER_RULES, {
    libraryRuleId,
    instrument,
    customConfigJson,
  });
}

/**
 * Update user rule enabled status
 */
export async function updateUserRuleEnabled(auth, callback, userRuleId, enabled) {
  if (USE_MOCK) {
    callback({ success: true });
    return;
  }
  await postRequestBackendDataV2(
    auth,
    callback,
    ENDPOINTS.CONCEPTGATE.USER_RULE_ENABLED(userRuleId),
    { enabled },
    'PATCH'
  );
}

/**
 * Remove a rule from user's selections
 */
export async function deleteUserRule(auth, callback, userRuleId) {
  if (USE_MOCK) {
    callback({ success: true });
    return;
  }
  await deleteRequestBackendDataV2(auth, callback, ENDPOINTS.CONCEPTGATE.USER_RULE(userRuleId));
}

/**
 * Update user rule custom configuration
 * @param {Object} auth - Firebase auth object
 * @param {Function} callback - Callback function to handle result
 * @param {string} userRuleId - UUID of the user rule
 * @param {Object} customConfigJson - Configuration object to store
 */
export async function updateUserRuleConfig(auth, callback, userRuleId, customConfigJson) {
  if (USE_MOCK) {
    callback({ id: userRuleId, customConfigJson: JSON.stringify(customConfigJson) });
    return;
  }
  await postRequestBackendDataV2(
    auth,
    callback,
    ENDPOINTS.CONCEPTGATE.USER_RULE_CONFIG(userRuleId),
    { customConfigJson: JSON.stringify(customConfigJson) },
    'PATCH'
  );
}

/**
 * Test a single rule against current trade (without persisting)
 * Used for previewing rule behavior during configuration
 * @param {Object} auth - Firebase auth object
 * @param {Function} callback - Callback function to handle result
 * @param {string} userRuleId - UUID of the user rule to test
 */
export async function testUserRule(auth, callback, userRuleId) {
  if (USE_MOCK) {
    callback({
      id: 'mock-test-execution',
      ruleName: 'Test Rule',
      ruleType: RuleType.HARDCODED,
      status: RuleExecutionStatus.PASSED,
      message: 'Mock test passed',
      suggestedFix: null,
      durationMs: 5,
    });
    return;
  }
  await postRequestBackendDataV2(
    auth,
    callback,
    ENDPOINTS.CONCEPTGATE.USER_RULE_TEST(userRuleId),
    {}
  );
}

// ============================================================================
// CONFIG API
// ============================================================================

/**
 * Get global LLM instructions (read-only for non-admin)
 * These instructions are prepended to all LLM rule prompts
 */
export async function getGlobalLlmInstructions(auth, callback) {
  if (USE_MOCK) {
    callback({ instructions: 'You are a trading validation assistant. Always provide fact-based reasoning.' });
    return;
  }
  await getRequestBackendDataV2(auth, callback, ENDPOINTS.CONCEPTGATE.LLM_INSTRUCTIONS);
}

// ============================================================================
// VALIDATION API
// ============================================================================

/**
 * Run validation on current trade
 * Returns validation result with all rule executions
 * @param {Object} auth - Firebase auth object
 * @param {Function} callback - Callback function to handle result
 * @param {Object} tradeSpec - Optional trade spec to save before validation
 */
export async function validateTrade(auth, callback, tradeSpec = null) {
  if (USE_MOCK) {
    callback(getMockValidationResult());
    return;
  }
  // Send trade spec in body if provided, otherwise send empty object
  const body = tradeSpec || {};
  await postRequestBackendDataV2(auth, callback, ENDPOINTS.CONCEPTGATE.VALIDATE, body);
}

/**
 * Get validation by ID (with rule executions)
 */
export async function getValidation(auth, callback, validationId) {
  if (USE_MOCK) {
    callback(getMockValidationResult());
    return;
  }
  await getRequestBackendDataV2(auth, callback, ENDPOINTS.CONCEPTGATE.VALIDATION(validationId));
}

/**
 * Get recent validations
 */
export async function getRecentValidations(auth, callback, limit = 10) {
  if (USE_MOCK) {
    callback([]);
    return;
  }
  await getRequestBackendDataV2(
    auth,
    callback,
    `${ENDPOINTS.CONCEPTGATE.VALIDATIONS}?limit=${limit}`
  );
}

// ============================================================================
// USAGE API
// ============================================================================

/**
 * Get usage summary (validations, tokens, cost)
 */
export async function getUsageSummary(auth, callback, days = 30) {
  if (USE_MOCK) {
    callback({ validationCount: 0, totalTokens: 0, totalCost: 0 });
    return;
  }
  await getRequestBackendDataV2(
    auth,
    callback,
    `${ENDPOINTS.CONCEPTGATE.USAGE_SUMMARY}?days=${days}`
  );
}

/**
 * Get usage breakdown by model
 */
export async function getUsageByModel(auth, callback, days = 30) {
  if (USE_MOCK) {
    callback({ usageByModel: [] });
    return;
  }
  await getRequestBackendDataV2(
    auth,
    callback,
    `${ENDPOINTS.CONCEPTGATE.USAGE_BY_MODEL}?days=${days}`
  );
}

/**
 * Get subscription status (tier, usage limits, current usage)
 */
export async function getSubscriptionStatus(auth, callback) {
  if (USE_MOCK) {
    callback({
      staticUsed: 2,
      staticLimit: 5,
      aiUsed: 0,
      aiLimit: 0,
      canValidateStatic: true,
      canValidateAi: false,
      tierName: 'Free',
      tierId: 'free',
      maxPipelinesPerInstrument: 1,
    });
    return;
  }
  await getRequestBackendDataV2(auth, callback, ENDPOINTS.CONCEPTGATE.SUBSCRIPTION);
}

// ============================================================================
// PIPELINE API
// ============================================================================

/**
 * Get all pipelines for an instrument
 * @param {Object} auth - Firebase auth object
 * @param {Function} callback - Callback function
 * @param {string} instrument - Instrument symbol (e.g., 'EURUSD')
 */
export async function getPipelines(auth, callback, instrument) {
  if (USE_MOCK) {
    callback([
      { id: 'mock-pipeline-1', name: 'Default', description: 'Your default rules', isActive: true, ruleCount: 3 },
    ]);
    return;
  }
  await getRequestBackendDataV2(
    auth,
    callback,
    `${ENDPOINTS.CONCEPTGATE.PIPELINES}?instrument=${encodeURIComponent(instrument)}`
  );
}

/**
 * Get the active pipeline for an instrument (creates default if none exists)
 * @param {Object} auth - Firebase auth object
 * @param {Function} callback - Callback function
 * @param {string} instrument - Instrument symbol
 */
export async function getActivePipeline(auth, callback, instrument) {
  if (USE_MOCK) {
    callback({ id: 'mock-pipeline-1', name: 'Default', description: 'Your default rules', isActive: true, ruleCount: 3 });
    return;
  }
  await getRequestBackendDataV2(
    auth,
    callback,
    `${ENDPOINTS.CONCEPTGATE.PIPELINES_ACTIVE}?instrument=${encodeURIComponent(instrument)}`
  );
}

/**
 * Get a specific pipeline by ID
 * @param {Object} auth - Firebase auth object
 * @param {Function} callback - Callback function
 * @param {string} pipelineId - Pipeline UUID
 */
export async function getPipeline(auth, callback, pipelineId) {
  if (USE_MOCK) {
    callback({ id: pipelineId, name: 'Default', description: 'Your default rules', isActive: true, ruleCount: 3 });
    return;
  }
  await getRequestBackendDataV2(auth, callback, ENDPOINTS.CONCEPTGATE.PIPELINE(pipelineId));
}

/**
 * Create a new pipeline
 * @param {Object} auth - Firebase auth object
 * @param {Function} callback - Callback function
 * @param {string} instrument - Instrument symbol
 * @param {string} name - Pipeline name
 * @param {string} description - Optional description
 */
export async function createPipeline(auth, callback, { instrument, name, description }) {
  if (USE_MOCK) {
    callback({ id: 'mock-new-pipeline', instrument, name, description, isActive: false, ruleCount: 0 });
    return;
  }
  await postRequestBackendDataV2(auth, callback, ENDPOINTS.CONCEPTGATE.PIPELINES, {
    instrument,
    name,
    description,
  });
}

/**
 * Update a pipeline's name and/or description
 * @param {Object} auth - Firebase auth object
 * @param {Function} callback - Callback function
 * @param {string} pipelineId - Pipeline UUID
 * @param {string} name - New name (optional)
 * @param {string} description - New description (optional)
 */
export async function updatePipeline(auth, callback, pipelineId, { name, description }) {
  if (USE_MOCK) {
    callback({ id: pipelineId, name, description });
    return;
  }
  await postRequestBackendDataV2(
    auth,
    callback,
    ENDPOINTS.CONCEPTGATE.PIPELINE(pipelineId),
    { name, description },
    'PUT'
  );
}

/**
 * Set a pipeline as active for its instrument
 * @param {Object} auth - Firebase auth object
 * @param {Function} callback - Callback function
 * @param {string} pipelineId - Pipeline UUID
 * @param {string} instrument - Instrument symbol
 */
export async function setActivePipeline(auth, callback, pipelineId, instrument) {
  if (USE_MOCK) {
    callback({ message: 'Pipeline activated' });
    return;
  }
  await postRequestBackendDataV2(
    auth,
    callback,
    `${ENDPOINTS.CONCEPTGATE.PIPELINE_ACTIVATE(pipelineId)}?instrument=${encodeURIComponent(instrument)}`,
    {},
    'PATCH'
  );
}

/**
 * Delete a pipeline
 * @param {Object} auth - Firebase auth object
 * @param {Function} callback - Callback function
 * @param {string} pipelineId - Pipeline UUID
 */
export async function deletePipeline(auth, callback, pipelineId) {
  if (USE_MOCK) {
    callback({ success: true });
    return;
  }
  await deleteRequestBackendDataV2(auth, callback, ENDPOINTS.CONCEPTGATE.PIPELINE(pipelineId));
}

/**
 * Duplicate a pipeline with all its rules
 * @param {Object} auth - Firebase auth object
 * @param {Function} callback - Callback function
 * @param {string} pipelineId - Pipeline UUID to duplicate
 * @param {string} newName - Name for the new pipeline
 */
export async function duplicatePipeline(auth, callback, pipelineId, newName) {
  if (USE_MOCK) {
    callback({ id: 'mock-duplicated-pipeline', name: newName, isActive: false, ruleCount: 3 });
    return;
  }
  await postRequestBackendDataV2(
    auth,
    callback,
    ENDPOINTS.CONCEPTGATE.PIPELINE_DUPLICATE(pipelineId),
    { name: newName }
  );
}

/**
 * Get rules for a specific pipeline
 * @param {Object} auth - Firebase auth object
 * @param {Function} callback - Callback function
 * @param {string} pipelineId - Pipeline UUID
 */
export async function getPipelineRules(auth, callback, pipelineId) {
  if (USE_MOCK) {
    callback([]);
    return;
  }
  await getRequestBackendDataV2(auth, callback, ENDPOINTS.CONCEPTGATE.PIPELINE_RULES(pipelineId));
}

/**
 * Add a rule to a specific pipeline
 * @param {Object} auth - Firebase auth object
 * @param {Function} callback - Callback function
 * @param {string} pipelineId - Pipeline UUID
 * @param {string} libraryRuleId - Library rule UUID to add
 * @param {Object} customConfigJson - Optional custom configuration
 */
export async function addRuleToPipeline(auth, callback, pipelineId, { libraryRuleId, customConfigJson }) {
  if (USE_MOCK) {
    callback({ id: 'mock-rule-id', pipelineId, libraryRuleId });
    return;
  }
  await postRequestBackendDataV2(auth, callback, ENDPOINTS.CONCEPTGATE.PIPELINE_RULES(pipelineId), {
    libraryRuleId,
    customConfigJson,
  });
}

/**
 * Reorder rules within a pipeline
 * @param {Object} auth - Firebase auth object
 * @param {Function} callback - Callback function
 * @param {string} pipelineId - Pipeline UUID
 * @param {Array} ruleOrders - Array of { ruleId, executionOrder } objects
 */
export async function reorderPipelineRules(auth, callback, pipelineId, ruleOrders) {
  if (USE_MOCK) {
    callback(ruleOrders.map(r => ({ id: r.ruleId, executionOrder: r.executionOrder })));
    return;
  }
  await postRequestBackendDataV2(
    auth,
    callback,
    ENDPOINTS.CONCEPTGATE.PIPELINE_RULES_REORDER(pipelineId),
    ruleOrders,
    'PUT'
  );
}

// ============================================================================
// PIPELINE WEBHOOK API
// ============================================================================

/**
 * Enable or disable webhook for a pipeline
 * @param {Object} auth - Firebase auth object
 * @param {Function} callback - Callback function
 * @param {string} pipelineId - Pipeline UUID
 * @param {boolean} enabled - Whether to enable or disable webhook
 */
export async function updatePipelineWebhook(auth, callback, pipelineId, enabled) {
  if (USE_MOCK) {
    callback({
      id: pipelineId,
      webhookEnabled: enabled,
      webhookUrl: enabled ? `/api/v2/webhook/pipeline/${pipelineId}/mock-secret` : null,
    });
    return;
  }
  await postRequestBackendDataV2(
    auth,
    callback,
    ENDPOINTS.CONCEPTGATE.PIPELINE_WEBHOOK(pipelineId),
    { enabled },
    'PATCH'
  );
}

/**
 * Regenerate webhook secret for a pipeline
 * @param {Object} auth - Firebase auth object
 * @param {Function} callback - Callback function
 * @param {string} pipelineId - Pipeline UUID
 */
export async function regeneratePipelineWebhookSecret(auth, callback, pipelineId) {
  if (USE_MOCK) {
    callback({
      id: pipelineId,
      webhookEnabled: true,
      webhookUrl: `/api/v2/webhook/pipeline/${pipelineId}/new-mock-secret`,
    });
    return;
  }
  await postRequestBackendDataV2(
    auth,
    callback,
    ENDPOINTS.CONCEPTGATE.PIPELINE_WEBHOOK_REGENERATE(pipelineId),
    {}
  );
}

/**
 * Get webhook usage stats for the user
 * @param {Object} auth - Firebase auth object
 * @param {Function} callback - Callback function
 */
export async function getWebhookUsage(auth, callback) {
  if (USE_MOCK) {
    callback({
      remainingCalls: 95,
      dailyLimit: 100,
      isUnlimited: false,
    });
    return;
  }
  await getRequestBackendDataV2(auth, callback, ENDPOINTS.CONCEPTGATE.WEBHOOK_USAGE);
}

// ============================================================================
// API CALL HISTORY
// ============================================================================

export const CallerType = {
  UI: 'UI',
  MCP: 'MCP',
  WEBHOOK: 'WEBHOOK',
};

/**
 * Get validation API call history
 * @param {Object} auth - Firebase auth object
 * @param {Function} callback - Callback function
 * @param {number} limit - Max number of entries to return (default 50)
 * @param {string} callerType - Optional filter by caller type (UI, MCP, WEBHOOK)
 */
export async function getCallHistory(auth, callback, { limit = 50, callerType = null } = {}) {
  if (USE_MOCK) {
    callback([
      {
        id: 'mock-history-1',
        callerType: 'UI',
        instrument: 'EURUSD',
        direction: 'LONG',
        responseStatus: 'GO',
        durationMs: 150,
        createdAt: new Date().toISOString(),
      },
      {
        id: 'mock-history-2',
        callerType: 'WEBHOOK',
        instrument: 'GBPUSD',
        direction: 'SHORT',
        responseStatus: 'NO_GO',
        durationMs: 220,
        createdAt: new Date(Date.now() - 3600000).toISOString(),
      },
    ]);
    return;
  }
  let endpoint = `${ENDPOINTS.CONCEPTGATE.HISTORY}?limit=${limit}`;
  if (callerType) {
    endpoint += `&callerType=${encodeURIComponent(callerType)}`;
  }
  await getRequestBackendDataV2(auth, callback, endpoint);
}

/**
 * Get a specific history entry with full details
 * @param {Object} auth - Firebase auth object
 * @param {Function} callback - Callback function
 * @param {string} historyId - History entry UUID
 */
export async function getCallHistoryEntry(auth, callback, historyId) {
  if (USE_MOCK) {
    callback({
      id: historyId,
      callerType: 'UI',
      instrument: 'EURUSD',
      direction: 'LONG',
      responseStatus: 'GO',
      durationMs: 150,
      requestPayload: '{"instrument":"EURUSD","direction":"LONG","entryPrice":1.085}',
      responsePayload: '{"validationId":"abc","status":"GO","rulesPassed":3}',
      createdAt: new Date().toISOString(),
    });
    return;
  }
  await getRequestBackendDataV2(auth, callback, ENDPOINTS.CONCEPTGATE.HISTORY_ENTRY(historyId));
}

/**
 * Get call history statistics by caller type
 * @param {Object} auth - Firebase auth object
 * @param {Function} callback - Callback function
 * @param {number} days - Number of days to look back (default 7)
 */
export async function getCallHistoryStats(auth, callback, days = 7) {
  if (USE_MOCK) {
    callback([
      { callerType: 'UI', count: 25 },
      { callerType: 'WEBHOOK', count: 10 },
      { callerType: 'MCP', count: 5 },
    ]);
    return;
  }
  await getRequestBackendDataV2(auth, callback, `${ENDPOINTS.CONCEPTGATE.HISTORY_STATS}?days=${days}`);
}

/**
 * Get daily statistics for history graph
 * @param {Object} auth - Firebase auth object
 * @param {Function} callback - Callback function
 * @param {number} days - Number of days to look back (default 7)
 */
export async function getCallHistoryDailyStats(auth, callback, days = 7) {
  // Get timezone offset in minutes (negate because JS returns UTC-local, we need local-UTC)
  const tzOffset = -new Date().getTimezoneOffset();

  if (USE_MOCK) {
    // Generate mock data for the last N days using local timezone
    const mockData = [];
    const today = new Date();
    const formatDateKey = (d) => {
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };
    for (let i = 0; i < days; i++) {
      const date = new Date(today);
      date.setDate(date.getDate() - i);
      const dateStr = formatDateKey(date);
      mockData.push(
        { date: dateStr, status: 'GO', pipelineName: 'Default', count: Math.floor(Math.random() * 10) },
        { date: dateStr, status: 'NO_GO', pipelineName: 'Default', count: Math.floor(Math.random() * 5) },
        { date: dateStr, status: 'GO', pipelineName: 'Scalping', count: Math.floor(Math.random() * 8) },
        { date: dateStr, status: 'NO_GO', pipelineName: 'Scalping', count: Math.floor(Math.random() * 3) }
      );
    }
    callback(mockData);
    return;
  }
  await getRequestBackendDataV2(auth, callback, `${ENDPOINTS.CONCEPTGATE.HISTORY_DAILY_STATS}?days=${days}&tzOffset=${tzOffset}`);
}

/**
 * Get hourly stats for today's validation calls (for "Today" filter)
 * @param {Object} auth - Firebase auth object
 * @param {Function} callback - Callback function
 */
export async function getCallHistoryHourlyStats(auth, callback) {
  // Get timezone offset in minutes (negate because JS returns UTC-local, we need local-UTC)
  const tzOffset = -new Date().getTimezoneOffset();

  if (USE_MOCK) {
    // Generate mock hourly data for today
    const mockData = [];
    const currentHour = new Date().getHours();
    for (let hour = 0; hour <= currentHour; hour++) {
      mockData.push(
        { hour, status: 'GO', pipelineName: 'Default', count: Math.floor(Math.random() * 5) },
        { hour, status: 'NO_GO', pipelineName: 'Default', count: Math.floor(Math.random() * 3) }
      );
    }
    callback(mockData);
    return;
  }
  await getRequestBackendDataV2(auth, callback, `${ENDPOINTS.CONCEPTGATE.HISTORY_HOURLY_STATS}?tzOffset=${tzOffset}`);
}

/**
 * Get unique pipelines for filtering history
 * @param {Object} auth - Firebase auth object
 * @param {Function} callback - Callback function
 */
export async function getHistoryPipelines(auth, callback) {
  if (USE_MOCK) {
    callback([
      { pipelineId: 'pipe-1', pipelineName: 'Default' },
      { pipelineId: 'pipe-2', pipelineName: 'Scalping' },
      { pipelineId: 'pipe-3', pipelineName: 'Swing' },
    ]);
    return;
  }
  await getRequestBackendDataV2(auth, callback, ENDPOINTS.CONCEPTGATE.HISTORY_PIPELINES);
}

/**
 * Get display info for caller type
 */
export function getCallerTypeDisplayInfo(callerType) {
  const info = {
    [CallerType.UI]: {
      label: 'Web UI',
      color: '#3498db',
      icon: 'desktop',
    },
    [CallerType.MCP]: {
      label: 'MCP (AI Agent)',
      color: '#9b59b6',
      icon: 'smart_toy',
    },
    [CallerType.WEBHOOK]: {
      label: 'Webhook',
      color: '#27ae60',
      icon: 'webhook',
    },
  };
  return info[callerType] || { label: callerType, color: '#95a5a6', icon: 'api' };
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Create a trade spec object
 */
export function createTradeSpec({
  instrument = 'EURUSD',
  direction = Direction.LONG,
  entryPrice,
  stopPrice,
  targetPrice,
  riskPercent,
  riskDollar,
  desiredRr,
}) {
  return {
    instrument,
    direction,
    entryPrice,
    stopPrice,
    targetPrice,
    riskPercent,
    riskDollar,
    desiredRr,
  };
}

/**
 * Get display info for a rule category
 */
export function getCategoryDisplayInfo(category) {
  const info = {
    [RuleCategory.RISK]: {
      label: 'Risk Management',
      icon: 'shield',
      color: '#e74c3c',
    },
    [RuleCategory.TECHNICAL]: {
      label: 'Technical Analysis',
      icon: 'chart',
      color: '#3498db',
    },
    [RuleCategory.TIMING]: {
      label: 'Timing',
      icon: 'clock',
      color: '#f39c12',
    },
    [RuleCategory.PSYCHOLOGY]: {
      label: 'Psychology',
      icon: 'brain',
      color: '#9b59b6',
    },
  };
  return info[category] || { label: category, icon: 'rule', color: '#95a5a6' };
}

/**
 * Get display info for execution status
 */
export function getStatusDisplayInfo(status) {
  const info = {
    [RuleExecutionStatus.PASSED]: {
      label: 'Passed',
      color: '#27ae60',
      icon: 'check',
    },
    [RuleExecutionStatus.FAILED]: {
      label: 'Failed',
      color: '#e74c3c',
      icon: 'x',
    },
    [RuleExecutionStatus.SKIPPED]: {
      label: 'Skipped',
      color: '#95a5a6',
      icon: 'minus',
    },
    [RuleExecutionStatus.ERROR]: {
      label: 'Error',
      color: '#e67e22',
      icon: 'alert',
    },
  };
  return info[status] || { label: status, color: '#95a5a6', icon: 'question' };
}

/**
 * Get display info for validation status
 */
export function getValidationStatusDisplayInfo(status) {
  const info = {
    [ValidationStatus.GO]: {
      label: 'GO',
      color: '#27ae60',
      bgColor: 'rgba(39, 174, 96, 0.1)',
      icon: 'check-circle',
    },
    [ValidationStatus.NO_GO]: {
      label: 'NO GO',
      color: '#e74c3c',
      bgColor: 'rgba(231, 76, 60, 0.1)',
      icon: 'x-circle',
    },
    [ValidationStatus.NEEDS_INFO]: {
      label: 'NEEDS INFO',
      color: '#f39c12',
      bgColor: 'rgba(243, 156, 18, 0.1)',
      icon: 'help-circle',
    },
  };
  return info[status] || { label: status, color: '#95a5a6', bgColor: 'rgba(149, 165, 166, 0.1)', icon: 'question' };
}

// ============================================================================
// MOCK DATA (for development)
// ============================================================================

function getMockLibraryRules() {
  return [
    {
      id: '00000000-0000-0000-0000-000000000001',
      name: 'Max Risk Per Trade',
      description: 'Ensures risk per trade does not exceed maximum allowed percentage',
      category: 'risk',
      instrument: null,
      ruleType: 'HARDCODED',
      enabled: true,
      ruleClass: 'MaxRiskGate',
      defaultConfigJson: '{"maxRiskPercent": 0.5}',
      executionOrder: 10,
      failFast: false,
    },
    {
      id: '00000000-0000-0000-0000-000000000003',
      name: 'Minimum R:R',
      description: 'Ensures reward-to-risk ratio meets minimum requirement',
      category: 'risk',
      instrument: null,
      ruleType: 'HARDCODED',
      enabled: true,
      ruleClass: 'MinRRGate',
      defaultConfigJson: '{"minRR": 1.5}',
      executionOrder: 30,
      failFast: false,
    },
    {
      id: '00000000-0000-0000-0001-000000000001',
      name: 'Trend Alignment',
      description: 'Validates trade direction aligns with higher timeframe trend',
      category: 'technical',
      instrument: 'EURUSD',
      ruleType: 'LLM_AGENT',
      enabled: true,
      agentModel: 'gpt-4o-mini',
      executionOrder: 100,
      failFast: false,
    },
  ];
}

function getMockValidationResult() {
  return {
    id: 'mock-validation-id',
    status: ValidationStatus.GO,
    startedAt: new Date().toISOString(),
    completedAt: new Date().toISOString(),
    totalDurationMs: 150,
    totalRulesExecuted: 2,
    rulesPassed: 2,
    rulesFailed: 0,
    rulesSkipped: 0,
    totalTokens: 0,
    totalPromptTokens: 0,
    totalCompletionTokens: 0,
    estimatedCostUsd: 0,
    ruleExecutions: [
      {
        id: 'exec-1',
        ruleName: 'Max Risk Per Trade',
        ruleType: 'HARDCODED',
        status: RuleExecutionStatus.PASSED,
        message: 'Risk 0.50% is within limit of 0.50%',
        suggestedFix: null,
        durationMs: 5,
      },
      {
        id: 'exec-2',
        ruleName: 'Minimum R:R',
        ruleType: 'HARDCODED',
        status: RuleExecutionStatus.PASSED,
        message: 'R:R of 1.83 meets minimum of 1.50',
        suggestedFix: null,
        durationMs: 3,
      },
    ],
  };
}

// Legacy exports for backward compatibility
export const updateConceptGateTrade = saveConceptGateTrade;
export const validateConceptGateTrade = validateTrade;

// Legacy settings functions (now uses library rules)
// These are kept for backward compatibility during migration
export async function getConceptGateSettings(auth, callback) {
  // In the new model, settings are replaced by library rules
  // Return a mock settings object that mirrors the old structure
  if (USE_MOCK) {
    callback({
      maxRiskEnabled: true,
      maxRiskPercent: 0.5,
      maxOpenRiskEnabled: false,
      maxOpenRiskPercent: 3.0,
      minRREnabled: true,
      minRR: 1.5,
      eventBlockEnabled: false,
      eventBlockBeforeMin: 30,
      eventBlockAfterMin: 5,
      liquidityBlockEnabled: false,
    });
    return;
  }
  // Fetch library rules and convert to legacy settings format
  await getLibraryRules(auth, (rules) => {
    if (rules.error) {
      callback(rules);
      return;
    }
    const settings = convertRulesToLegacySettings(rules);
    callback(settings);
  });
}

export async function updateConceptGateSettings(auth, callback, data) {
  // In the new model, we would update user rule configs
  // For now, just return the data as-is
  callback({ ...data, updatedAt: new Date().toISOString() });
}

export async function resetConceptGateSettings(auth, callback) {
  // Return default settings
  callback({
    maxRiskEnabled: true,
    maxRiskPercent: 0.5,
    maxOpenRiskEnabled: false,
    maxOpenRiskPercent: 3.0,
    minRREnabled: true,
    minRR: 1.5,
    eventBlockEnabled: false,
    liquidityBlockEnabled: false,
    updatedAt: new Date().toISOString(),
  });
}

function convertRulesToLegacySettings(rules) {
  const settings = {
    maxRiskEnabled: false,
    maxRiskPercent: 0.5,
    maxOpenRiskEnabled: false,
    maxOpenRiskPercent: 3.0,
    minRREnabled: false,
    minRR: 1.5,
    eventBlockEnabled: false,
    eventBlockBeforeMin: 30,
    eventBlockAfterMin: 5,
    liquidityBlockEnabled: false,
  };

  for (const rule of rules) {
    if (rule.ruleClass === 'MaxRiskGate') {
      settings.maxRiskEnabled = rule.enabled;
      try {
        const config = JSON.parse(rule.defaultConfigJson || '{}');
        settings.maxRiskPercent = config.maxRiskPercent || 0.5;
      } catch (e) { }
    } else if (rule.ruleClass === 'MaxOpenRiskGate') {
      settings.maxOpenRiskEnabled = rule.enabled;
      try {
        const config = JSON.parse(rule.defaultConfigJson || '{}');
        settings.maxOpenRiskPercent = config.maxOpenRiskPercent || 3.0;
      } catch (e) { }
    } else if (rule.ruleClass === 'MinRRGate') {
      settings.minRREnabled = rule.enabled;
      try {
        const config = JSON.parse(rule.defaultConfigJson || '{}');
        settings.minRR = config.minRR || 1.5;
      } catch (e) { }
    } else if (rule.ruleClass === 'EventProximityGate') {
      settings.eventBlockEnabled = rule.enabled;
    } else if (rule.ruleClass === 'LiquidityWindowGate') {
      settings.liquidityBlockEnabled = rule.enabled;
    }
  }

  return settings;
}

// Legacy GateStatus (maps to RuleExecutionStatus)
export const GateStatus = {
  PASSED: 'PASSED',
  FAILED: 'FAILED',
  SKIPPED: 'SKIPPED',
  DISABLED: 'DISABLED', // Maps to SKIPPED in new API
};

// Legacy getRuleDisplayInfo (for backward compatibility)
// Handles both old rule type format (MAX_RISK_PER_TRADE) and new rule name format (Max Risk Per Trade)
export function getRuleDisplayInfo(ruleType) {
  const info = {
    // Old format (rule types)
    'MAX_RISK_PER_TRADE': {
      label: 'Risk Per Trade',
      description: 'Maximum risk allowed per single trade',
      icon: 'percentage',
    },
    'MAX_OPEN_RISK': {
      label: 'Max Open Risk',
      description: 'Maximum total open risk across all positions',
      icon: 'chart',
    },
    'MIN_REWARD_RISK': {
      label: 'Minimum R:R',
      description: 'Minimum reward-to-risk ratio required',
      icon: 'target',
    },
    'EVENT_PROXIMITY': {
      label: 'Event Block',
      description: 'Block trades near high-impact events',
      icon: 'calendar',
    },
    'LIQUIDITY_WINDOW': {
      label: 'Liquidity Window',
      description: 'Block trades during low liquidity periods',
      icon: 'clock',
    },
    // New format (rule names from backend)
    'Max Risk Per Trade': {
      label: 'Risk Per Trade',
      description: 'Maximum risk allowed per single trade',
      icon: 'percentage',
    },
    'Max Open Risk': {
      label: 'Max Open Risk',
      description: 'Maximum total open risk across all positions',
      icon: 'chart',
    },
    'Minimum R:R': {
      label: 'Minimum R:R',
      description: 'Minimum reward-to-risk ratio required',
      icon: 'target',
    },
    'Event Proximity': {
      label: 'Event Block',
      description: 'Block trades near high-impact events',
      icon: 'calendar',
    },
    'Liquidity Window': {
      label: 'Liquidity Window',
      description: 'Block trades during low liquidity periods',
      icon: 'clock',
    },
    'Trend Alignment': {
      label: 'Trend Alignment',
      description: 'Validates trade direction aligns with higher timeframe trend',
      icon: 'trending_up',
    },
    'Key Level Proximity': {
      label: 'Key Level Proximity',
      description: 'Checks if entry price is near significant support/resistance levels',
      icon: 'show_chart',
    },
  };
  return info[ruleType] || { label: ruleType || 'Unknown Rule', description: '', icon: 'rule' };
}
